import { useApolloClient } from '@apollo/client';
import { Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { Route, Routes } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AuthUser, UserModuleIDs } from '@enroller/utils';
import { getUserModules } from '@enroller/utils';

import { routes } from '.';

import { ContentWithHeaderRoute } from '../layout/ContentWithHeaderRoute';
import { getStudentFamilyId, mapStudentApplicationResponse } from '../student/application/StudentApplication';
import { StudentDetailsForm } from '../student/application/studentDetails/StudentDetailsForm';
import { FamilyForm } from '../student/application/family/FamilyForm';
import { BiographyForm } from '../student/application/biography/BiographyForm';
import { HousingForm } from '../student/application/housing/HousingForm';
import { DearFamilyForm } from '../student/application/dearFamily/DearFamilyForm';
import { PhotoAlbumForm } from '../student/application/photoAlbum/PhotoAlbumForm';
import { DocumentsForm } from '../student/application/documents/DocumentsForm';
import { InterviewForm } from '../student/application/interview/InterviewForm';
import { AgreementsForm } from '../student/application/agreements/AgreementsForm';
import { VaccinationsForm } from '../student/application/vaccinations/VaccinationsForm';
import { useStatelessPut } from '../utils/useStatelessPut';
import { useApplicationContext } from '../contexts/ApplicationContext/ApplicationContext';
import { SchoolAndLanguages } from '../student/application/schoolAndLanguages/SchoolAndLanguagesForm';

import { STUDENT_DETAILS_QUERY, SCHOOL_AND_LANGUAGES_QUERY } from '../student/application/queries/studentApplication.query';
import { HOUSING_FOOD_ALLERGIES_PETS_QUERY } from '../student/application/queries/pages';
import { StudentFamilyContactsGQLResponse, StudentFamilyGQLResponse, StudentGQLResponse } from '../student/application/queries/graphqlResponse';
import { STUDENT_FAMILY_CONTACTS_QUERY, STUDENT_FAMILY_QUERY } from '../student/application/queries/pages/studentFamily';

export const StudentApplicationRoutes = () => {
  const { user } = useAuth0<AuthUser>();
  const { query } = useApolloClient();
  const userModules = useMemo<UserModuleIDs | undefined>(() => user && getUserModules(user), [user]);

  // TODO: adjust to use middleware API
  const putUserApplication = useStatelessPut(`/api/student-applications/${userModules?.appId}`);

  const { applicationData, setApplicationData } = useApplicationContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchStudentApplication = useCallback(
    async ({ appId }: UserModuleIDs) => {
      try {
        setLoading(true);

        const [housingFoodAllergiesPetsResponse, studentDetailsResponse, schoolAndLanguagesResponse, studentFamilyResponse] = await Promise.all([
          query<StudentGQLResponse['housing']>({
            query: HOUSING_FOOD_ALLERGIES_PETS_QUERY,
            variables: { appId },
          }),
          query<StudentGQLResponse['studentDetails']>({
            query: STUDENT_DETAILS_QUERY,
            variables: { appId },
          }),
          query<StudentGQLResponse['schoolAndLanguages']>({
            query: SCHOOL_AND_LANGUAGES_QUERY,
            variables: { appId },
          }),
          query<StudentFamilyGQLResponse>({
            query: STUDENT_FAMILY_QUERY,
            variables: { appId },
          }),
        ]);

        // dependent queries e.g. Student Family Contacts, Interviews
        const [studentFamilyContactsResponse] = await Promise.all([
          query<StudentFamilyContactsGQLResponse>({
            query: STUDENT_FAMILY_CONTACTS_QUERY,
            variables: { studentFamilyId: getStudentFamilyId(studentFamilyResponse.data) },
          }),
        ]);

        const pagesData = mapStudentApplicationResponse({
          studentDetails: studentDetailsResponse?.data,
          housing: housingFoodAllergiesPetsResponse?.data,
          schoolAndLanguages: schoolAndLanguagesResponse?.data,
          family: { data: studentFamilyResponse.data, contacts: studentFamilyContactsResponse.data },
        });

        setApplicationData(pagesData);
      } catch (error) {
        console.error('Error fetching student application:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [query, setApplicationData],
  );

  useEffect(() => {
    // TODO: verify triggers to re-fetch data
    if (userModules) {
      fetchStudentApplication({ appId: userModules.appId });
    }
  }, [fetchStudentApplication, userModules]);

  const subRoutes = routes.application.subRoutes?.[0].routes;

  if (loading) return <Typography>Loading</Typography>;

  if (error) return <Typography color="error">Error loading Student Application</Typography>;

  return (
    <Routes>
      <Route element={<ContentWithHeaderRoute />}>
        <Route
          path={subRoutes?.interview.url}
          element={<InterviewForm applicationData={applicationData?.interview} submitApplication={putUserApplication} />}
        />
        <Route
          path={subRoutes?.studentDetails.url}
          element={
            <StudentDetailsForm
              modulesID={applicationData?.modulesID}
              submitApplication={putUserApplication}
              studentDetails={applicationData?.studentDetails}
            />
          }
        />
        <Route
          path={subRoutes?.family.url}
          element={<FamilyForm applicationData={applicationData.family} modulesID={applicationData?.modulesID} submitApplication={putUserApplication} />}
        />
        <Route
          path={subRoutes?.biography.url}
          element={<BiographyForm applicationData={applicationData?.biography} submitApplication={putUserApplication} />}
        />
        <Route
          path={subRoutes?.schoolAndLanguages.url}
          element={
            <SchoolAndLanguages
              modulesID={applicationData?.modulesID}
              submitApplication={putUserApplication}
              schoolAndLanguages={applicationData?.schoolAndLanguages}
            />
          }
        />
        <Route
          path={subRoutes?.housing.url}
          element={<HousingForm modulesID={applicationData?.modulesID} housingData={applicationData?.housing} submitApplication={putUserApplication} />}
        />
        <Route path={subRoutes?.dearFamily.url} element={<DearFamilyForm />} />
        <Route path={subRoutes?.photoAlbum.url} element={<PhotoAlbumForm />} />
        <Route path={subRoutes?.documents.url} element={<DocumentsForm />} />
        <Route path={subRoutes?.agreements.url} element={<AgreementsForm />} />
        <Route path={subRoutes?.vaccinations.url} element={<VaccinationsForm />} />
      </Route>
    </Routes>
  );
};
